import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { Icon } from '@iconify/react/dist/iconify.js';
import { addLanguage, getCountries, updateLanguage } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-grid-container > div {
    width: 100% !important;
    box-sizing: border-box !important;
  }
  .form-control, select, textarea {
    width: 100% !important;
    box-sizing: border-box !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
    font-size: 0.875rem !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
  }
  .form-control:focus, select:focus, textarea:focus {
    border-color: #1a73e8 !important;
    box-shadow: 0 0 0 0.2rem rgba(26, 115, 232, 0.25) !important;
    outline: none !important;
  }
  .form-control:disabled, select:disabled {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    cursor: not-allowed !important;
  }
  .error-message {
    color: #dc3545;
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
  .success-message {
    color: #28a745;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    text-align: center;
  }
  .image-preview-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 1rem;
  }
  .image-preview {
    max-width: 120px;
    max-height: 120px;
    object-fit: contain;
    border-radius: 8px;
    border: 2px solid #e2e8f0;
    padding: 4px;
    background: white;
  }
  .preview-label {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.5rem;
  }
  .card-body {
    overflow-y: auto !important;
    max-height: calc(100vh - 80px) !important;
    padding: 1.5rem !important;
    overscroll-behavior: contain !important;
  }
  .drawer-header {
    padding: 1.5rem;
    border-bottom: 1px solid #dee2e6;
    position: relative;
  }
  .drawer-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a73e8;
  }
  .form-label {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: #4a4a4a !important;
    margin-bottom: 0.5rem !important;
    display: block;
  }
  .required-star {
    color: #dc3545;
  }
  .file-input-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
  }
  .file-input-label {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 16px;
    background: #f8f9fa;
    border: 1px dashed #ced4da;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .file-input-label:hover {
    background: #e9ecef;
    border-color: #1a73e8;
  }
  .file-input-label.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  .file-input {
    position: absolute;
    width: 0.1px;
    height: 0.1px;
    opacity: 0;
    overflow: hidden;
    z-index: -1;
  }
  .file-input-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #495057;
    font-size: 0.875rem;
  }
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
  .current-preview-section {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e2e8f0;
  }
  .edit-mode-notice {
    background-color: #fff3cd;
    border: 1px solid #ffc107;
    color: #856404;
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }
  .auth-error-alert {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 1rem;
  }
  .optional-badge {
    background-color: #6c757d;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    margin-left: 8px;
  }
  .country-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
  }
  .country-emoji {
    font-size: 1.25rem;
  }
`;

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.soksan-dss.com";

const fetchCountries = async () => {
  try {
    // Replace this with your actual API call
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const data = await getCountries(token);
    return data.countries || [];
  } catch (error) {
    console.error("Error fetching countries:", error);
    return [];
  }
};

const LanguageDrawer = ({ isOpen, onClose, languageToEdit, googleLanguages = [], onSuccess }) => {
    const [message, setMessage] = useState(null);
    const [currentImagePreview, setCurrentImagePreview] = useState(null);
    const [newImagePreview, setNewImagePreview] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [authError, setAuthError] = useState(false);
    const [countries, setCountries] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, trigger } = useForm({
        defaultValues: {
            LanguageName: languageToEdit?.LanguageName || '',
            LanguageCode: languageToEdit?.LanguageCode || '',
            GoogleLanguageCode: languageToEdit?.GoogleLanguageCode || '',
            CountryID: languageToEdit?.CountryID || '',
            ImageOfLanguageSymbol: null,
        },
    });

    const watchLanguageName = watch('LanguageName');

    const getAuthToken = () => {
        const token = localStorage.getItem("token");
        
        if (!token) {
            console.error("No authentication token found in localStorage");
            setAuthError(true);
            setMessage({
                type: 'error',
                text: 'Authentication token not found. Please login again.'
            });
            return null;
        }
        
        return token;
    };

    useEffect(() => {
        if (isOpen) {
            const loadCountries = async () => {
                const token = getAuthToken();
                if (token) {
                    setLoadingCountries(true);
                    try {
                        const countriesData = await fetchCountries(token);
                        setCountries(countriesData);
                        console.log("countries", countries)
                    } catch (error) {
                        console.error("Failed to load countries:", error);
                        setMessage({ type: 'error', text: 'Failed to load countries list' });
                    } finally {
                        setLoadingCountries(false);
                    }
                }
            };
            loadCountries();
        }
    }, [isOpen, languageToEdit]);

    useEffect(() => {
        if (!languageToEdit && watchLanguageName && googleLanguages.length > 0) {
            const selectedLang = googleLanguages.find(lang => lang.name === watchLanguageName);
            if (selectedLang) {
                setValue('GoogleLanguageCode', selectedLang.code);
                setValue('LanguageCode', selectedLang.code);
                trigger(['GoogleLanguageCode', 'LanguageCode']);
            }
        }
    }, [watchLanguageName, googleLanguages, languageToEdit, setValue, trigger]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setAuthError(false);
            setMessage(null);

            if (languageToEdit) {
                reset({
                    LanguageName: languageToEdit.LanguageName || '',
                    LanguageCode: languageToEdit.LanguageCode || '',
                    GoogleLanguageCode: languageToEdit.GoogleLanguageCode || '',
                    CountryID: languageToEdit.CountryID || '',
                    ImageOfLanguageSymbol: null,
                });

                // Set current image preview
                if (languageToEdit.ImageOfLanguageSymbol) {
                    const imageUrl = languageToEdit.ImageOfLanguageSymbol.startsWith('http')
                        ? languageToEdit.ImageOfLanguageSymbol
                        : `${API_BASE}/uploads/lang_sym/${languageToEdit.ImageOfLanguageSymbol}`;
                    setCurrentImagePreview(imageUrl);
                } else {
                    setCurrentImagePreview(null);
                }
                setNewImagePreview(null);
            } else {
                reset({
                    LanguageName: '',
                    LanguageCode: '',
                    GoogleLanguageCode: '',
                    CountryID: '',
                    ImageOfLanguageSymbol: null,
                });
                setCurrentImagePreview(null);
                setNewImagePreview(null);
            }
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, languageToEdit, reset]);

    // Handle file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setMessage({ type: 'error', text: 'Please select an image file' });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                return;
            }

            setValue('ImageOfLanguageSymbol', file, { shouldValidate: true });

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            
            setMessage(null);
        } else {
            setValue('ImageOfLanguageSymbol', null);
            setNewImagePreview(null);
        }
    };

    // Handle clicking the file input label to reset the input
    const handleImageLabelClick = (e) => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    // Form submission
    const onFormSubmit = async (data) => {
        try {
            setSaveLoading(true);
            setMessage(null);
            setAuthError(false);

            const token = getAuthToken();
            if (!token) {
                setSaveLoading(false);
                return;
            }

            if (languageToEdit) {
                if (!data.ImageOfLanguageSymbol) {
                    setMessage({ 
                        type: 'error', 
                        text: 'Please select a new image to update. If you don\'t want to change the image, click Cancel.' 
                    });
                    setSaveLoading(false);
                    return;
                }
            } else {
                // ADD MODE: Validate all fields including CountryID
                if (!data.LanguageName || !data.LanguageCode || !data.GoogleLanguageCode || !data.CountryID) {
                    setMessage({ type: 'error', text: 'Please fill all required fields' });
                    setSaveLoading(false);
                    return;
                }

                if (!data.ImageOfLanguageSymbol) {
                    setMessage({ type: 'error', text: 'Please select a language symbol image' });
                    setSaveLoading(false);
                    return;
                }
            }

            const formData = new FormData();

            if (languageToEdit) {
                if (data.ImageOfLanguageSymbol) {
                    formData.append('ImageOfLanguageSymbol', data.ImageOfLanguageSymbol);
                }
            } else {
                formData.append('LanguageName', data.LanguageName);
                formData.append('LanguageCode', data.LanguageCode);
                formData.append('GoogleLanguageCode', data.GoogleLanguageCode);
                formData.append('CountryID', data.CountryID);

                if (data.ImageOfLanguageSymbol) {
                    formData.append('ImageOfLanguageSymbol', data.ImageOfLanguageSymbol);
                }
            }

            let response;
            if (languageToEdit) {
                response = await updateLanguage(languageToEdit.ID, formData, token);
            } else {
                response = await addLanguage(formData, token);
            }

            if (response.success) {
                setMessage({
                    type: 'success',
                    text: languageToEdit
                        ? 'Language image updated successfully!'
                        : 'Language added successfully!'
                });

                if (onSuccess) {
                    onSuccess();
                }

                setTimeout(() => {
                    onClose();
                    reset();
                    setSaveLoading(false);
                }, 1500);
            } else {
                setMessage({
                    type: 'error',
                    text: response.message || 'Failed to save language'
                });
                setSaveLoading(false);
            }

        } catch (error) {
            console.error('Save error:', error);
            
            setMessage({
                type: 'error',
                text: error.response?.data?.message || error.message || 'An error occurred while saving. Please try again.'
            });
            setSaveLoading(false);
        }
    };

    const handleLoginRedirect = () => {
        localStorage.clear();
        navigate('/sign-in');
    };

    return (
        <>
            <style>{customStyles}</style>
            <Drawer
                open={isOpen}
                onClose={onClose}
                direction="right"
                size={600}
                style={{ zIndex: 1050 }}
                className="p-0"
            >
                <div className="drawer-header">
                    <h6 className="drawer-title">
                        {languageToEdit ? 'Edit Language' : 'Add New Language'}
                    </h6>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            right: '1rem',
                            top: '1rem',
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#6c757d'
                        }}
                    >
                        <Icon icon="lucide:x" />
                    </button>
                </div>

                <div className="card-body">
                    {authError && (
                        <div className="auth-error-alert">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Icon icon="mdi:alert-circle-outline" />
                                <strong>Authentication Error</strong>
                            </div>
                            <p>Your session has expired or you're not authorized to perform this action.</p>
                            <button 
                                className="btn btn-sm btn-danger"
                                onClick={handleLoginRedirect}
                            >
                                Go to Login
                            </button>
                        </div>
                    )}

                    {message && (
                        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
                            {message.text}
                        </div>
                    )}

                    {saveLoading && (
                        <div className="loading-overlay">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onFormSubmit)}>
                        {/* Language Name */}
                        <div className="mb-20">
                            <label htmlFor="LanguageName" className="form-label">
                                Language Name <span className="required-star">*</span>
                            </label>
                            {languageToEdit ? (
                                <input
                                    type="text"
                                    id="LanguageName"
                                    className="form-control"
                                    value={languageToEdit.LanguageName}
                                    disabled
                                    readOnly
                                />
                            ) : (
                                <>
                                    <select
                                        id="LanguageName"
                                        className={`form-select ${errors.LanguageName ? 'border-danger-600' : ''}`}
                                        {...register('LanguageName', {
                                            required: 'Language Name is required',
                                        })}
                                    >
                                        <option value="">Select Language</option>
                                        {googleLanguages.map((lang) => (
                                            <option key={lang.code} value={lang.name}>
                                                {lang.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.LanguageName && (
                                        <span className="error-message">{errors.LanguageName.message}</span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Google Language Code */}
                        <div className="mb-20">
                            <label htmlFor="GoogleLanguageCode" className="form-label">
                                Google Language Code <span className="required-star">*</span>
                            </label>
                            <input
                                type="text"
                                id="GoogleLanguageCode"
                                className={`form-control ${errors.GoogleLanguageCode ? 'border-danger-600' : ''}`}
                                {...register('GoogleLanguageCode', {
                                    required: 'Google Language Code is required',
                                })}
                                disabled={!!languageToEdit}
                                readOnly
                            />
                            {errors.GoogleLanguageCode && (
                                <span className="error-message">{errors.GoogleLanguageCode.message}</span>
                            )}
                        </div>

                        {/* Language Code */}
                        <div className="mb-20">
                            <label htmlFor="LanguageCode" className="form-label">
                                Language Code <span className="required-star">*</span>
                            </label>
                            <input
                                type="text"
                                id="LanguageCode"
                                className={`form-control ${errors.LanguageCode ? 'border-danger-600' : ''}`}
                                {...register('LanguageCode', {
                                    required: 'Language Code is required',
                                })}
                                disabled={!!languageToEdit}
                            />
                            {errors.LanguageCode && (
                                <span className="error-message">{errors.LanguageCode.message}</span>
                            )}
                        </div>

<div className="mb-20">
    <label htmlFor="CountryID" className="form-label">
        Country <span className="required-star">*</span>
    </label>
    
    {languageToEdit ? (
        // EDIT MODE: Show selected country as disabled input
        <div className="d-flex align-items-center gap-2">
            <input
                type="text"
                id="CountryID"
                className="form-control"
                value={
                    countries.find(c => c.ID === languageToEdit.CountryID)?.CountryName || 
                    languageToEdit.CountryName || 
                    'Country not found'
                }
                disabled
                readOnly
            />
            <input
                type="hidden"
                {...register('CountryID', {
                    value: languageToEdit.CountryID || '',
                })}
            />
        </div>
    ) : (
        <>
            <select
                id="CountryID"
                className={`form-select ${errors.CountryID ? 'border-danger-600' : ''}`}
                {...register('CountryID', {
                    required: 'Country is required',
                })}
                disabled={loadingCountries}
            >
                <option value="">Select Country</option>
                {loadingCountries ? (
                    <option value="" disabled>Loading countries...</option>
                ) : (
                    countries.map((country) => (
                        <option key={country.ID} value={country.ID}>
                            {country.CountryName} ({country.CountryCode})
                        </option>
                    ))
                )}
            </select>
            {errors.CountryID && (
                <span className="error-message">{errors.CountryID.message}</span>
            )}
        </>
    )}
</div>
                        

                        {/* Image Upload */}
                        <div className="mb-20">
                            <label className="form-label d-flex align-items-center">
                                Language Symbol Image <span className="required-star"> *</span>
                            </label>

                            <div className="file-input-wrapper">
                                <label
                                    htmlFor="imageUpload"
                                    className={`file-input-label ${saveLoading || authError ? 'disabled' : ''}`}
                                    onClick={handleImageLabelClick}
                                >
                                    <div className="file-input-text">
                                        <Icon icon="bi:image" />
                                        <span>
                                            {languageToEdit
                                                ? newImagePreview ? 'Change selected image' : 'Click to select new image'
                                                : newImagePreview ? 'Change selected image' : 'Click to upload image'}
                                        </span>
                                    </div>
                                </label>
                                <input
                                    ref={fileInputRef}
                                    id="imageUpload"
                                    type="file"
                                    accept="image/*"
                                    className="file-input"
                                    onChange={handleFileChange}
                                    disabled={saveLoading || authError}
                                    required={!languageToEdit}
                                />
                            </div>

                            {newImagePreview && (
                                <div className="image-preview-container">
                                    <img
                                        src={newImagePreview}
                                        alt="New language symbol preview"
                                        className="image-preview"
                                    />
                                </div>
                            )}

                            {languageToEdit && currentImagePreview && !newImagePreview && (
                                <div className="current-preview-section">
                                    <div className="image-preview-container">
                                        <img
                                            src={currentImagePreview}
                                            alt="Current language symbol"
                                            className="image-preview"
                                        />
                                        <span className="preview-label">Current Image</span>
                                    </div>
                                </div>
                            )}

                            {errors.ImageOfLanguageSymbol && (
                                <span className="error-message">{errors.ImageOfLanguageSymbol.message}</span>
                            )}
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                            <button
                                type="button"
                                className="btn btn-secondary px-48 py-10 radius-8"
                                onClick={onClose}
                                disabled={saveLoading}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary px-48 py-10 radius-8 d-flex align-items-center gap-2"
                                disabled={saveLoading || authError}
                            >
                                {saveLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm"></span>
                                        {languageToEdit ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    languageToEdit ? 'Update Image' : 'Save Language'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </Drawer>
        </>
    );
};

export default LanguageDrawer;